import type { SelectOption } from '@diamond/shared/types';
import { Text } from '@diamond/shared/ui';
import { reactSelectStyles, SelectAutocomplete } from '@diamond/shared/ui';
import { adminCaptureException } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { getProducts } from '@diamond/sol-admin/features/product';
import axios from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';

export interface ProductPickerProps {
  value: string | null;
  onChange: (option: SelectOption | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  debounce?: number;
}

export function ProductPicker({
  value,
  onChange,
  placeholder = 'Pilih Produk',
  isDisabled,
  debounce = 300,
}: ProductPickerProps) {
  const token = useAuthStore((s) => s.access_token);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const productsOptions = useMemo(() => options, [options]);
  const searchReqIdRef = useRef(0);

  const handleChange = useCallback(
    (_v: unknown, option: SelectOption | null) => {
      onChange(option);
    },
    [onChange]
  );

  const loadOptions = useCallback(
    (query: string) => {
      return new Promise<SelectOption[]>((resolve, reject) => {
        const reqId = ++searchReqIdRef.current;
        getProducts(token, { page: 1, size: 100, search_query: query })
          .then((val) => {
            if (reqId !== searchReqIdRef.current) return; // drop stale
            const data = val.data.map(
              (item: { id: string; title: string }) => ({
                label: item.title,
                value: item.id,
              })
            );
            resolve(data);
            setOptions(data);
          })
          .catch((e: unknown) => {
            if (reqId !== searchReqIdRef.current) return;
            const respData = (
              axios.isAxiosError(e) ? e.response?.data ?? {} : {}
            ) as { statusCode?: number; message?: string };
            adminCaptureException(
              {
                message:
                  respData.message ??
                  (e instanceof Error ? e.message : 'Unknown error'),
                statusCode:
                  respData.statusCode ??
                  (axios.isAxiosError(e) ? e.response?.status ?? 0 : 0),
              },
              (scope) => {
                scope.setTags({
                  feature: 'highlight',
                  action: 'productPickerLoad',
                  status_code: respData.statusCode,
                });
                scope.setContext('error', { error: respData });
                scope.setLevel('error');
              }
            );
            reject(e);
          });
      });
    },
    [token]
  );

  return (
    <SelectAutocomplete
      name="product"
      placeholder={placeholder}
      emptyMessage={<Text>Produk Tidak Ditemukan</Text>}
      debounce={debounce}
      options={productsOptions}
      chakraStyles={reactSelectStyles}
      onChange={handleChange}
      loadOptions={loadOptions}
      isDisabled={isDisabled}
      value={value}
    />
  );
}
